// ./src/components/features/VehicleTasks/VehicleTaskEditDrawer/VehicleTaskEditDrawer.tsx
//
// Unified edit drawer for Vehicle Tasks.
//
// Design:
// - Self-loading in edit mode via recordId (shows skeleton while loading)
// - Context-or-props resolution: reads get/create/update/isCreating/isUpdating/
//   lastError/clearError from VehicleTasksContext when available; props override.
// - onCreated/onUpdated are lightweight notifications for page-level
//   side effects (highlight, type tracking)
// - Dirty state tracking via form serialization
// - Collapsible Details and Recurrence sections
// - No local validators — server-side validation via ApiError

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Group,
  Divider,
  Text,
  Collapse,
  UnstyledButton,
  Skeleton,
} from "@mantine/core";
import { DatePickerInput, DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";

import { ApiError } from "@/utils/apiResultHandler";
import { utcToLocalString, localStringToUtc } from "@/utils/dateHelpers";

import {
  VehicleTask,
  VehicleTaskInput,
  VEHICLE_TASK_STATUS,
  VEHICLE_TASK_PRIORITY,
  ScheduleTypeValue,
  VehicleTaskPriorityValue,
  VehicleTaskStatusValue,
} from "@/utils/api/VehicleTaskService";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { useDashboardContext } from "@/contexts/DashboardContext";
import { useOptionalVehicleTasksContext } from "@/contexts/VehicleTasksContext";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { AppIcon } from "@/components/generic/AppIcon";
import { ScheduleDaysInput } from "@/components/generic/ScheduleDaysInput";
import { ScheduleTypeSelector } from "@/components/generic/ScheduleTypeSelector";

import { CarSelector } from "@/components/features/Cars/CarSelector";
import { UserSelector } from "@/components/features/Users/UserSelector";
import { PrioritySelector } from "@/components/features/VehicleTasks/PrioritySelector";
import { CategorySelector } from "@/components/features/VehicleTasks/CategorySelector";
import { VehicleTaskStatusSelector } from "@/components/features/VehicleTasks/VehicleTaskStatusSelector";

// ============================================
// Types
// ============================================

export interface VehicleTaskEditDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Record ID to load in edit mode (null/undefined for create mode) */
  recordId?: string | null;

  /**
   * Optional override for loading a record by ID.
   * If not provided, uses VehicleTasksContext.get when available.
   */
  onLoad?: (id: string) => Promise<OpResult>;

  /**
   * Optional override for creating a record.
   * If not provided, uses VehicleTasksContext.create when available.
   */
  onCreate?: (data: VehicleTaskInput) => Promise<OpResult>;

  /**
   * Optional override for updating a record.
   * If not provided, uses VehicleTasksContext.update when available.
   */
  onUpdate?: (id: string, data: VehicleTaskInput) => Promise<OpResult>;

  /**
   * Called after a record is successfully created.
   * Receives the created VehicleTask so the page can react
   * (e.g. highlight the new row).
   */
  onCreated?: (task: VehicleTask) => void;

  /**
   * Called after a record is successfully updated.
   * Receives the updated VehicleTask so the page can react
   * (e.g. highlight the updated row).
   */
  onUpdated?: (task: VehicleTask) => void;

  /** Whether create operation is in progress (prop-based override) */
  isCreating?: boolean;

  /** Whether update operation is in progress (prop-based override) */
  isUpdating?: boolean;

  /** Last error from operations (prop-based override) */
  lastError?: ApiError | null;

  /** Callback to clear errors (prop-based override) */
  onClearError?: () => void;

  /** Pre-selected car ID (from URL param or context) */
  initialCarId?: string | null;
}

// ============================================
// Form Data Interface
// ============================================

interface FormData {
  carId: string | null;
  assignedToUserId: string | null;
  title: string;
  notes: string;
  category: string | null;
  priority: number;
  status: number;
  dueDate: string | null;
  reminderDate: string | null;
  scheduleType: ScheduleTypeValue | null;
  scheduleDays: string;
}

interface ExpandedSections {
  schedule: boolean;
  details: boolean;
}

// ============================================
// Constants
// ============================================

const KNOWN_FORM_FIELDS = [
  "carId",
  "assignedToUserId",
  "title",
  "notes",
  "category",
  "priority",
  "status",
  "dueDate",
  "reminderDate",
  "scheduleType",
  "scheduleDays",
];

// ============================================
// Helper Functions
// ============================================

/**
 * Convert UTC datetime string from API to local date string for DatePickerInput.
 */
const utcToDatePickerString = (
  utcDateString: string | null | undefined
): string | null => {
  if (!utcDateString) return null;
  const localString = utcToLocalString(utcDateString);
  return localString ? localString.split(" ")[0] : null;
};

/**
 * Convert local date string from DatePickerInput to UTC ISO string for API.
 */
const datePickerStringToUtc = (
  localDateString: string | null
): string | null => {
  if (!localDateString) return null;
  return localStringToUtc(`${localDateString} 00:00`);
};

/**
 * Convert UTC datetime to local datetime string for DateTimePicker.
 */
const utcToDateTimePickerString = (
  utcDateString: string | null | undefined
): string | null => {
  if (!utcDateString) return null;
  return utcToLocalString(utcDateString);
};

/**
 * Convert local datetime string from DateTimePicker to UTC ISO string for API.
 */
const dateTimePickerStringToUtc = (
  localDateTimeString: string | null
): string | null => {
  if (!localDateTimeString) return null;
  return localStringToUtc(localDateTimeString);
};

const getInitialFormData = (): FormData => ({
  carId: null,
  assignedToUserId: null,
  title: "",
  notes: "",
  category: null,
  priority: VEHICLE_TASK_PRIORITY.LOW,
  status: VEHICLE_TASK_STATUS.TODO,
  dueDate: null,
  reminderDate: null,
  scheduleType: null,
  scheduleDays: "",
});

const getInitialExpandedSections = (): ExpandedSections => ({
  schedule: false,
  details: false,
});

// ============================================
// Dirty-state serializer (stable key order)
// ============================================

const serializeFormSnapshot = (data: FormData): string => {
  return JSON.stringify(data);
};

/**
 * Invoke both error-clearing callbacks, but avoid calling the same
 * function reference twice (happens when no prop overrides are provided
 * and both resolve to vehicleTasksContext.clearError).
 */
const clearAllErrors = (
  onClearError: (() => void) | undefined,
  clearOperationError: (() => void) | undefined
) => {
  onClearError?.();
  if (clearOperationError && clearOperationError !== onClearError) {
    clearOperationError();
  }
};

// ============================================
// Loading Skeleton Component
// ============================================

const VehicleTaskEditDrawerSkeleton: React.FC = () => {
  return (
    <Stack gap="md">
      {/* Title */}
      <Skeleton height={60} radius="sm" />

      {/* Priority */}
      <Skeleton height={60} radius="sm" />

      {/* Category */}
      <Skeleton height={60} radius="sm" />

      {/* Vehicle */}
      <Skeleton height={60} radius="sm" />

      {/* Due Date */}
      <Skeleton height={60} radius="sm" />

      {/* Status */}
      <Skeleton height={60} radius="sm" />

      {/* Collapsible sections */}
      <Divider />
      <Skeleton height={20} width={100} />
      <Divider />
      <Skeleton height={20} width={120} />
    </Stack>
  );
};

// ============================================
// Section Header Component
// ============================================

interface SectionHeaderProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  optional?: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  expanded,
  onToggle,
  optional = false,
}) => (
  <UnstyledButton onClick={onToggle} w="100%">
    <Group justify="space-between" py="xs">
      <Group gap="xs">
        <Text fw={600} size="sm">
          {title}
        </Text>
        {optional && (
          <Text size="xs" c="dimmed">
            ({optional})
          </Text>
        )}
      </Group>
      <AppIcon iconName={expanded ? "ChevronUp" : "ChevronDown"} size={16} />
    </Group>
  </UnstyledButton>
);

// ============================================
// Component
// ============================================

export const VehicleTaskEditDrawer: React.FC<VehicleTaskEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  onLoad,
  onCreate,
  onUpdate,
  onCreated,
  onUpdated,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
  initialCarId,
}) => {
  const { t } = useTranslation(["vehicleTasks", "common"]);
  const { totalCarsCount } = useDashboardContext();
  const { isMobile } = useAppEnvironment();

  // ============================================
  // Context vs Props resolution
  // ============================================

  const vehicleTasksContext = useOptionalVehicleTasksContext();

  const loadFn = onLoad ?? vehicleTasksContext?.get;
  const createFn = onCreate ?? vehicleTasksContext?.create;
  const updateFn = onUpdate ?? vehicleTasksContext?.update;
  const creating = isCreating || (vehicleTasksContext?.isCreating ?? false);
  const updating = isUpdating || (vehicleTasksContext?.isUpdating ?? false);
  const operationError = lastError ?? vehicleTasksContext?.lastError ?? null;
  const clearOperationError = onClearError ?? vehicleTasksContext?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  // ============================================
  // Stable refs for callbacks used in effects
  // ============================================

  const clearOperationErrorRef = useRef(clearOperationError);
  clearOperationErrorRef.current = clearOperationError;

  const onClearErrorRef = useRef(onClearError);
  onClearErrorRef.current = onClearError;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;

  const tRef = useRef(t);
  tRef.current = t;

  // ============================================
  // Self-loading state
  // ============================================

  const [record, setRecord] = useState<VehicleTask | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [isReadyToLoad, setIsReadyToLoad] = useState(false);

  useEffect(() => {
    if (opened) {
      const frameId = requestAnimationFrame(() => {
        setIsReadyToLoad(true);
      });
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsReadyToLoad(false);
    }

    return;
  }, [opened]);

  const showLoadingSkeleton = isLoadingRecord && !record;

  // ============================================
  // Load record when opened in edit mode
  // ============================================

  useEffect(() => {
    let isCancelled = false;

    if (isReadyToLoad && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      const loadRecord = async () => {
        const result = await loadFnRef.current!(recordId);

        if (isCancelled) return;

        if (result.didSucceed()) {
          const loaded = result.getDataFirst() as VehicleTask;
          setRecord(loaded);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(ApiError.fromResult(result, tRef.current("vehicleTasks:form.loadError")));
        }

        setIsLoadingRecord(false);
      };

      loadRecord();
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [isReadyToLoad, opened, isEditMode, recordId]);

  // ============================================
  // Form State
  // ============================================

  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>(
    () => getInitialExpandedSections()
  );

  // ============================================
  // Dirty State Tracking
  // ============================================

  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton) return;
    const current = serializeFormSnapshot(formData);
    setIsDirty(current !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton]);

  // ============================================
  // Error Handling (ApiError integration)
  // ============================================

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(() => {
    return ApiError.getFormErrors(effectiveError, KNOWN_FORM_FIELDS);
  }, [effectiveError]);

  const formattedGenericError = useMemo(() => {
    return ApiError.formatErrors(genericErrors);
  }, [genericErrors]);

  // ============================================
  // Form Reset on Open
  // ============================================

  useEffect(() => {
    if (!opened) return;

    if (isEditMode && !record) return;

    let newFormData: FormData;

    if (record) {
      // ── Edit mode ──
      newFormData = {
        carId: record.carId || null,
        assignedToUserId: record.assignedToUserId || null,
        title: record.title || "",
        notes: record.notes || "",
        category: record.category || null,
        priority: record.priority ?? VEHICLE_TASK_PRIORITY.LOW,
        status: record.status ?? VEHICLE_TASK_STATUS.TODO,
        dueDate: utcToDatePickerString(record.dueDate),
        reminderDate: utcToDateTimePickerString(record.reminderDate),
        scheduleType: (record.scheduleType as ScheduleTypeValue) || null,
        scheduleDays: record.scheduleDays || "",
      };

      setExpandedSections({
        schedule: Boolean(record.scheduleType),
        details:
          Boolean(record.notes) ||
          Boolean(record.reminderDate) ||
          Boolean(record.assignedToUserId),
      });
    } else {
      // ── Create mode ──
      newFormData = getInitialFormData();

      if (initialCarId) {
        newFormData.carId = initialCarId;
      }

      setExpandedSections(getInitialExpandedSections());
    }

    setFormData(newFormData);
    initialSnapshotRef.current = serializeFormSnapshot(newFormData);
    setIsDirty(false);
    clearAllErrors(onClearErrorRef.current, clearOperationErrorRef.current);
  }, [opened, record, initialCarId, isEditMode]);

  // ============================================
  // Handlers
  // ============================================

  const handleFieldChange = useCallback(
    (field: string | undefined, value: string | number | null) => {
      if (!field) return;
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      handleFieldChange(name, value);
    },
    [handleFieldChange]
  );

  const handleScheduleTypeChange = useCallback(
    (_name: string, value: string | number | null) => {
      setFormData((prev) => ({
        ...prev,
        scheduleType: (value as ScheduleTypeValue) || null,
        scheduleDays: "",
      }));
    },
    []
  );

  const handleDueDateChange = useCallback(
    (value: string | null) => {
      handleFieldChange("dueDate", value);
    },
    [handleFieldChange]
  );

  const handleReminderDateChange = useCallback(
    (value: string | null) => {
      handleFieldChange("reminderDate", value);
    },
    [handleFieldChange]
  );

  const handleScheduleDaysChange = useCallback(
    (value: string) => {
      handleFieldChange("scheduleDays", value);
    },
    [handleFieldChange]
  );

  const toggleSection = useCallback((section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // ============================================
  // Submit
  // ============================================

  const handleSubmit = useCallback(async () => {
    clearAllErrors(onClearError, clearOperationError);
    setLoadError(null);

    const submitData: VehicleTaskInput = {
      carId: formData.carId,
      assignedToUserId: formData.assignedToUserId,
      title: formData.title.trim() || undefined,
      notes: formData.notes.trim() || undefined,
      category: formData.category || undefined,
      priority: formData.priority as VehicleTaskPriorityValue,
      status: formData.status as VehicleTaskStatusValue,
      dueDate: datePickerStringToUtc(formData.dueDate) || undefined,
      reminderDate: dateTimePickerStringToUtc(formData.reminderDate) || undefined,
      scheduleType: formData.scheduleType || undefined,
      scheduleDays: formData.scheduleDays || undefined,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);

      const savedRecord = result.data ? (result.data as VehicleTask[])[0] : null;
      if (savedRecord) {
        if (isCreate) {
          onCreated?.(savedRecord);
        } else {
          onUpdated?.(savedRecord);
        }
      }

      onClose();
    }
  }, [
    formData,
    isEditMode,
    recordId,
    createFn,
    updateFn,
    onClose,
    onClearError,
    clearOperationError,
    onCreated,
    onUpdated,
  ]);

  // ============================================
  // Drawer title
  // ============================================

  const drawerTitle = useMemo(() => {
    return isEditMode
      ? t("vehicleTasks:form.editTitle")
      : t("vehicleTasks:form.addTitle");
  }, [isEditMode, t]);

  const dateDropdownType = isMobile ? "modal" : "popover";

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      responsive
      mobileHeightPercent={92}
      closable={!isSubmitting && !isLoadingRecord}
      loading={isSubmitting}
      errors={formattedGenericError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmDisabled={showLoadingSkeleton}
      isDirty={isDirty}
    >
      {showLoadingSkeleton ? (
        <VehicleTaskEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {/* ============================================ */}
          {/* CORE FIELDS - Always visible */}
          {/* ============================================ */}

          {/* Title */}
          <TextInput
            label={t("vehicleTasks:form.fields.title.label")}
            placeholder={t("vehicleTasks:form.fields.title.placeholder")}
            name="title"
            value={formData.title}
            onChange={handleTextChange}
            error={fieldErrors.title}
            required
            size="md"
            autoFocus={!isEditMode}
          />

          {/* Priority */}
          <PrioritySelector
            label={t("vehicleTasks:form.fields.priority.label")}
            name="priority"
            value={formData.priority}
            onChange={handleFieldChange}
            error={fieldErrors.priority}
            required
            size="md"
          />

          {/* Category */}
          <CategorySelector
            label={t("vehicleTasks:form.fields.category.label")}
            name="category"
            value={formData.category}
            onChange={handleFieldChange}
            error={fieldErrors.category}
            size="md"
          />

          {/* Vehicle */}
          <CarSelector
            label={t("vehicleTasks:form.fields.car.label")}
            description={t("vehicleTasks:form.fields.car.description")}
            name="carId"
            value={formData.carId}
            onChange={handleFieldChange}
            error={fieldErrors.carId}
            disabled={totalCarsCount === 1}
            clearable
            size="md"
          />

          {/* Due Date */}
          <DatePickerInput
            label={t("vehicleTasks:form.fields.dueDate.label")}
            placeholder={t("vehicleTasks:form.fields.dueDate.placeholder")}
            value={formData.dueDate}
            onChange={handleDueDateChange}
            error={fieldErrors.dueDate}
            valueFormat="MMM DD, YYYY"
            clearable
            size="md"
            dropdownType={dateDropdownType}
          />

          {/* Status (edit mode only) */}
          {isEditMode && (
            <VehicleTaskStatusSelector
              label={t("vehicleTasks:form.fields.status.label")}
              value={formData.status as VehicleTaskStatusValue}
              onChange={(val) => handleFieldChange("status", val)}
              error={fieldErrors.status}
              required
              size="md"
            />
          )}

          {/* ============================================ */}
          {/* DETAILS SECTION - Collapsible */}
          {/* ============================================ */}

          <Divider />
          <SectionHeader
            title={t("vehicleTasks:form.sections.details")}
            expanded={expandedSections.details}
            onToggle={() => toggleSection("details")}
          />
          <Collapse in={expandedSections.details}>
            <Stack gap="sm">
              {/* Notes */}
              <Textarea
                label={t("common:fields.notes")}
                placeholder={t("vehicleTasks:form.fields.notes.placeholder")}
                name="notes"
                value={formData.notes}
                onChange={handleTextChange}
                error={fieldErrors.notes}
                minRows={2}
                maxRows={5}
                autosize
                size="md"
              />

              {/* Assigned User */}
              <UserSelector
                label={t("vehicleTasks:form.fields.assignedTo.label")}
                name="assignedToUserId"
                value={formData.assignedToUserId}
                onChange={handleFieldChange}
                error={fieldErrors.assignedToUserId}
                size="md"
              />

              {/* Reminder Date/Time */}
              <DateTimePicker
                label={t("vehicleTasks:form.fields.reminderDate.label")}
                placeholder={t("vehicleTasks:form.fields.reminderDate.placeholder")}
                value={formData.reminderDate}
                onChange={handleReminderDateChange}
                error={fieldErrors.reminderDate}
                valueFormat="MMM DD, YYYY hh:mm A"
                clearable
                size="md"
                dropdownType={dateDropdownType}
              />
            </Stack>
          </Collapse>

          {/* ============================================ */}
          {/* RECURRENCE SECTION - Collapsible */}
          {/* ============================================ */}

          <Divider />
          <SectionHeader
            title={t("vehicleTasks:form.sections.recurrence")}
            expanded={expandedSections.schedule}
            onToggle={() => toggleSection("schedule")}
          />
          <Collapse in={expandedSections.schedule}>
            <Stack gap="sm">
              {/* Schedule Type */}
              <ScheduleTypeSelector
                label={t("vehicleTasks:form.fields.scheduleType.label")}
                name="scheduleType"
                value={formData.scheduleType}
                onChange={handleScheduleTypeChange}
                error={fieldErrors.scheduleType}
                size="md"
                showOneTime={false}
              />

              {/* Schedule Days (dynamic based on type) */}
              {formData.scheduleType && (
                <ScheduleDaysInput
                  scheduleType={formData.scheduleType}
                  value={formData.scheduleDays}
                  onChange={handleScheduleDaysChange}
                  label={t("vehicleTasks:form.fields.scheduleDays.label")}
                  error={fieldErrors.scheduleDays}
                  size="md"
                />
              )}

              {/* Recurrence hint */}
              <Text size="xs" c="dimmed" fs="italic">
                {t("vehicleTasks:form.recurrenceHint")}
              </Text>
            </Stack>
          </Collapse>
        </Stack>
      )}
    </AppDrawer>
  );
};